import { useEffect, useMemo, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"

import apiClient from "src/apiClient"
import { selectGameConfig } from "src/store/common"
import { setGame, setSpectatingAs } from "src/store/game"
import { redactForViewer } from "src/utils/fog"
import { buildTimeline, facingsByTick, finalFacings } from "src/utils/roundMotion"
import { asksForStillness } from "src/components/Canvas"
import { REPLAY_BEAT_MS, cameraCentre, gameFromStep, playableOf, roundHoldMs } from "src/utils/replay"

/* Review mode's engine (owner's ask, 2026-09-07: one view for the game, the game just finished,
   and a replay opened from the history). Fetches `GET /game/{id}/steps/` — or takes it preloaded,
   from the replay route, which has to have it before the game view can mount at all — picks a
   tick and a seat, and loads the chosen snapshot into the store as the game, so every panel the
   game view mounts reads it exactly as it reads a live round. Inert while `active` is false: a
   live game never fetches its own history.

   Through the fog or over it (owner's ask, 2026-09-06): the steps arrive whole — the war is over
   — so the fog here is the client's own (`utils/fog`), run over the snapshot for the seat being
   watched, whatever the server's switch says. Off by default: a review opens as the spectator's
   view, the whole board, and the toggle is how you ask what the seat actually knew. */
const NO_FACINGS = {}

const useReplay = ({ gameId, active, preloaded = null, defaultSeat = null, openAt = "last", showSpeed = 1 }) => {
  const dispatch = useDispatch()
  const gameConfig = useSelector(selectGameConfig)
  const [data, setData] = useState(preloaded)
  const [failed, setFailed] = useState(false)
  const [tick, setTick] = useState(null)
  const [playing, setPlaying] = useState(false)
  const [seatId, setSeatId] = useState(null)
  const [fogOn, setFogOn] = useState(false)

  useEffect(() => {
    if (preloaded) setData(preloaded)
  }, [preloaded])

  useEffect(() => {
    if (!active || preloaded || !gameId) return undefined
    let cancelled = false
    apiClient
      .get(`/game/${gameId}/steps/`)
      .then((response) => !cancelled && setData(response.data))
      .catch(() => !cancelled && setFailed(true))
    return () => {
      cancelled = true
    }
  }, [active, preloaded, gameId])

  const playable = useMemo(() => playableOf(data), [data])

  // Where it opens, once the steps are in: the final round for a game that has just ended, the
  // first snapshot for a replay opened from the history.
  useEffect(() => {
    if (tick != null || !playable.length) return
    setTick(openAt === "first" ? playable[0].tick : playable[playable.length - 1].tick)
  }, [playable, tick, openAt])

  // Whose eyes: the player's own seat when they have one, else the first seat at the table.
  useEffect(() => {
    if (seatId != null || !data?.players?.length) return
    const seats = data.players.map((seat) => seat.id)
    setSeatId(seats.includes(defaultSeat) ? defaultSeat : seats[0])
  }, [data, seatId, defaultSeat])

  const rawStep = playable.find((step) => step.tick === tick) || null
  const shownIndex = playable.findIndex((step) => step.tick === tick)
  const step = useMemo(() => {
    if (!rawStep || !fogOn || seatId == null) return rawStep
    return {
      ...rawStep,
      metadata: redactForViewer({ ...rawStep.metadata, tick: rawStep.tick }, seatId, gameConfig?.rules_config, {
        past: true,
      }),
    }
  }, [rawStep, fogOn, seatId, gameConfig])

  /* Which way the standing stacks face on the round shown: what every show up to it left them in
     (`roundMotion.facingsByTick`), as a live browser keeps it. Through a seat's fog, only that
     side's own stacks keep a heading from the rounds before; an enemy's comes from what the side
     saw of this round's show, the log `redactForViewer` cut. */
  const facingsAll = useMemo(() => facingsByTick(playable), [playable])
  const facings = useMemo(() => {
    const all = facingsAll[tick] || NO_FACINGS
    if (!fogOn || seatId == null || !step) return all
    const users = rawStep?.metadata?.users || {}
    const team = users[String(seatId)]?.team
    const own = Object.fromEntries(
      Object.entries(all).filter(([key]) => team != null && users[key.split("@")[0]]?.team === team)
    )
    const motion = step.metadata?.motion
    return motion?.steps?.length ? { ...own, ...finalFacings(buildTimeline(motion)) } : own
  }, [facingsAll, tick, fogOn, seatId, step, rawStep])

  /* The snapshot becomes the store's game, and the chosen seat becomes the viewer. Both are set
     together and in this order: every panel reads the seat *out of* the game, so a seat set
     against the previous round's snapshot renders one frame of the wrong board. */
  useEffect(() => {
    if (!active || !data || !step || seatId == null) return
    dispatch(setGame([gameFromStep(data, step)]))
    dispatch(setSpectatingAs(seatId))
  }, [dispatch, active, data, step, seatId])

  /* Autoplay: each round held for as long as its show takes, then the next (`roundHoldMs`). A round
     autoplay moved to has its show played as it lands and is held for all of it; the round on
     screen when play is pressed has had its show, and waits one beat. */
  const playedTo = useRef(null)
  const holdMs = useMemo(
    () => (playedTo.current === tick ? roundHoldMs(step, { viewerId: seatId, still: asksForStillness(), speed: showSpeed }) : REPLAY_BEAT_MS),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [step, seatId, tick, showSpeed]
  )
  useEffect(() => {
    if (!playing) playedTo.current = null
    if (!playing || playable.length < 2) return undefined
    const timer = setTimeout(() => {
      const index = playable.findIndex((each) => each.tick === tick)
      if (index < 0 || index >= playable.length - 1) {
        setPlaying(false)
        return
      }
      playedTo.current = playable[index + 1].tick
      setTick(playable[index + 1].tick)
    }, holdMs)
    return () => clearTimeout(timer)
  }, [playing, playable, tick, holdMs])

  const initialPosition = useMemo(() => cameraCentre(playable[0]), [playable])

  return {
    data,
    failed,
    playable,
    tick,
    setTick,
    shownIndex,
    playing,
    setPlaying,
    seatId,
    setSeatId,
    fogOn,
    setFogOn,
    initialPosition,
    facings,
  }
}

export default useReplay
